import { useRef, useEffect, useMemo } from 'react';
import styles from './PeriodTimeline.module.css';

/**
 * A horizontal, scrollable timeline scrubber for picking one period out of a
 * chronological list — the Aktienfinder-style rail of ticks under the
 * Income-Statement Sankey card.
 *
 * The rail scales from a handful of annual periods to ~40+ quarterly ones. To
 * stay legible at quarterly density we borrow the reference's year-legend
 * trick rather than copy its slider: every period is a tick, but only *year*
 * boundaries carry a label underneath (annual: every tick; quarterly: the tick
 * at Q1 of each year). The selected period always shows a floating bubble with
 * its full label ("Q2 2026") above the rail, so you know exactly what's active
 * even mid-year. Overflow scrolls horizontally — the parent's prev/next arrows
 * and the arrow keys move the selection, and the active tick auto-centers — and
 * edge-fade masks (see CSS) signal there's more off-screen.
 *
 * Presentational: the selected `index` and `onSelect` are owned by the parent
 * (Analysis.js).
 *
 * @param {Array<{label: string, period?: string, fiscalYear?: (number|string)}>} periods - chronological (oldest-first)
 * @param {number} index - currently-selected index into `periods`
 * @param {(index: number) => void} onSelect
 * @param {'annual'|'quarterly'} cadence
 * @param {string} ariaLabel
 */
const PeriodTimeline = ({ periods = [], index, onSelect, cadence = 'annual', ariaLabel = 'Period' }) => {
  const railRef = useRef(null);

  // Precompute, per tick: the short year label to show underneath (or null to
  // hide it) and whether this tick is a year boundary (gets a taller/darker
  // tick so years read as anchors across a long quarterly rail). Annual: every
  // tick is its own year. Quarterly: the boundary is Q1 (or the first tick of a
  // year we haven't labelled yet, so a series that starts mid-year still gets
  // an anchor). Falls back to parsing the trailing 4-digit year out of `label`
  // when the structured `fiscalYear`/`period` fields aren't present.
  const ticks = useMemo(() => {
    let lastLabelledYear = null;
    return periods.map((p) => {
      const year = p.fiscalYear != null
        ? String(p.fiscalYear)
        : (String(p.label).match(/(\d{4})/) || [])[1] || null;
      const isQ1 = typeof p.period === 'string' && /^Q0*1$/i.test(p.period.trim());

      let isBoundary;
      let yearLabel = null;
      if (cadence === 'annual') {
        isBoundary = true;
        yearLabel = year;
      } else {
        // First tick of a not-yet-labelled year, preferring an actual Q1.
        const firstOfYear = year != null && year !== lastLabelledYear;
        isBoundary = isQ1 || firstOfYear;
        if (isBoundary && firstOfYear) yearLabel = year;
      }
      if (yearLabel != null) lastLabelledYear = year;
      return { year, yearLabel, isBoundary };
    });
  }, [periods, cadence]);

  // Keep the selected tick centered within the rail as the selection moves (via
  // the card's prev/next arrows, arrow keys, or a cadence switch that jumps to
  // the latest). We set the rail's own scrollLeft directly rather than calling
  // scrollIntoView(): scrollIntoView walks up and scrolls every scrollable
  // ancestor, including the page, so on initial load (when this rail sits below
  // the fold) it would yank the whole document down. Setting scrollLeft only
  // moves the rail horizontally and never touches the page's vertical scroll.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const active = rail.querySelector(`[data-active="true"]`);
    if (!active) return;
    const targetLeft = active.offsetLeft - (rail.clientWidth - active.clientWidth) / 2;
    rail.scrollLeft = Math.max(0, targetLeft);
  }, [index, periods, cadence]);

  if (!periods.length) return null;

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      onSelect(index - 1);
    } else if (e.key === 'ArrowRight' && index < periods.length - 1) {
      e.preventDefault();
      onSelect(index + 1);
    }
  };

  return (
    <div className={styles.railViewport}>
      <div
        className={styles.rail}
        data-cadence={cadence}
        ref={railRef}
        role="group"
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
      >
        {periods.map((p, i) => {
          const active = i === index;
          const { yearLabel, isBoundary } = ticks[i];
          const dotClass = [
            styles.dot,
            active ? styles.dotActive : '',
            isBoundary ? styles.dotBoundary : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={p.label + i}
              type="button"
              data-active={active}
              className={dotClass}
              aria-pressed={active}
              aria-label={p.label}
              title={p.label}
              onClick={() => onSelect(i)}
            >
              {active && <span className={styles.bubble}>{p.label}</span>}
              <span className={styles.tick} aria-hidden="true" />
              <span className={styles.yearLabel} aria-hidden="true">
                {yearLabel || '\u00a0'}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PeriodTimeline;
